/**
 * Secure Token Storage Utility
 *
 * Follows SOLID Principles:
 * - Single Responsibility: Each function has one clear purpose
 * - Open/Closed: Extensible through interfaces, closed for modification
 * - Dependency Inversion: Depends on abstractions (localStorage interface)
 *
 * Provides encrypted storage for authentication tokens using AES encryption.
 * Falls back to plain storage during migration for backward compatibility.
 */

import CryptoJS from 'crypto-js';

// ============================================================================
// Interfaces (Interface Segregation Principle)
// ============================================================================

/**
 * Interface for encryption operations
 */
interface IEncryptionService {
  encrypt(data: string): string;
  decrypt(encryptedData: string): string | null;
}

/**
 * Interface for token storage operations
 */
interface ITokenStorage {
  set(key: string, token: string | null): void;
  get(key: string): string | null;
  remove(key: string): void;
}

/**
 * Interface for token migration operations
 */
interface ITokenMigration {
  migrate(): { accessToken: string | null; refreshToken: string | null };
}

// ============================================================================
// Encryption Service (Single Responsibility: Encryption only)
// ============================================================================

/**
 * Encryption Service Implementation
 * Single Responsibility: Handles encryption/decryption only
 */
class EncryptionService implements IEncryptionService {
  private readonly encryptionPrefix = 'enc:';

  /**
   * Gets encryption key derived from domain and salt
   */
  private getEncryptionKey(): string {
    // Use a normalized domain to ensure consistency across subdomains
    const getNormalizedDomain = (): string => {
      if (typeof window === 'undefined') return 'nexgo-ai';

      const hostname = window.location.hostname.toLowerCase();

      // Normalize different hostname variations to the same domain
      if (hostname.includes('nexgo.ai')) {
        return 'nexgo-ai';
      }
      if (hostname.includes('localhost')) {
        return 'localhost';
      }

      // Fallback to actual hostname for other domains
      const normalized = hostname.replace(/\./g, '-');
      console.log('[ENCRYPTION] Normalized domain:', {
        original: hostname,
        normalized,
      });
      return normalized;
    };

    const domain = getNormalizedDomain();
    const salt =
      process.env.NEXT_PUBLIC_ENCRYPTION_SALT || 'nexgo-auth-salt-2024';
    const baseKey = `${domain}-${salt}`;
    // Generate stable key using SHA256
    return CryptoJS.SHA256(baseKey).toString();
  }

  /**
   * Encrypts a token
   */
  encrypt(data: string): string {
    try {
      const key = this.getEncryptionKey();
      const encrypted = CryptoJS.AES.encrypt(data, key).toString();
      return `${this.encryptionPrefix}${encrypted}`;
    } catch (error) {
      console.error('[EncryptionService] Encryption failed:', error);
      // Return unencrypted data as fallback
      return data;
    }
  }

  /**
   * Decrypts an encrypted token
   */
  decrypt(encryptedData: string): string | null {
    if (!encryptedData) return null;

    try {
      // Check if data is encrypted
      if (!encryptedData.startsWith(this.encryptionPrefix)) {
        // Legacy plain token - return as is for backward compatibility
        return encryptedData;
      }

      const encrypted = encryptedData.substring(this.encryptionPrefix.length);
      const key = this.getEncryptionKey();
      const decrypted = CryptoJS.AES.decrypt(encrypted, key);
      const data = decrypted.toString(CryptoJS.enc.Utf8);

      if (!data) {
        console.warn('[EncryptionService] Decryption resulted in empty data');
        return null;
      }

      return data;
    } catch (error) {
      console.error('[EncryptionService] Decryption failed:', error);
      // Try to return as plain token for backward compatibility
      return encryptedData.startsWith(this.encryptionPrefix)
        ? null
        : encryptedData;
    }
  }
}

// ============================================================================
// Secure Token Storage (Single Responsibility: Storage operations only)
// ============================================================================

/**
 * Secure Token Storage Implementation
 * Single Responsibility: Handles storage operations only
 * Dependency Inversion: Depends on IEncryptionService abstraction
 */
class SecureTokenStorage implements ITokenStorage {
  private readonly encryptionService: IEncryptionService;
  private readonly storage: Storage;

  constructor(encryptionService: IEncryptionService, storage: Storage) {
    this.encryptionService = encryptionService;
    this.storage = storage;
  }

  /**
   * Securely stores a token
   */
  set(key: string, token: string | null): void {
    if (typeof window === 'undefined') return;

    try {
      if (token) {
        const encrypted = this.encryptionService.encrypt(token);
        this.storage.setItem(key, encrypted);
      } else {
        this.storage.removeItem(key);
      }
    } catch (error) {
      console.error(`[SecureTokenStorage] Failed to set token ${key}:`, error);
      // Fallback to plain storage if secure storage fails
      if (token) {
        this.storage.setItem(key, token);
      } else {
        this.storage.removeItem(key);
      }
    }
  }

  /**
   * Securely retrieves a token
   */
  get(key: string): string | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = this.storage.getItem(key);
      if (!stored) return null;

      return this.encryptionService.decrypt(stored);
    } catch (error) {
      console.error(`[SecureTokenStorage] Failed to get token ${key}:`, error);
      // Fallback to plain retrieval
      return this.storage.getItem(key);
    }
  }

  /**
   * Removes a token
   */
  remove(key: string): void {
    if (typeof window === 'undefined') return;

    try {
      this.storage.removeItem(key);
      // Also remove legacy plain tokens if they exist
      const legacyKey = key.replace('secure-', '');
      if (legacyKey !== key) {
        this.storage.removeItem(legacyKey);
      }
    } catch (error) {
      console.error(
        `[SecureTokenStorage] Failed to remove token ${key}:`,
        error
      );
    }
  }
}

// ============================================================================
// Token Migration Service (Single Responsibility: Migration only)
// ============================================================================

/**
 * Token Migration Service
 * Single Responsibility: Handles migration of legacy tokens
 * Dependency Inversion: Depends on ITokenStorage abstraction
 */
class TokenMigrationService implements ITokenMigration {
  private readonly tokenStorage: ITokenStorage;
  private readonly legacyKeys: { access: string; refresh: string };
  private readonly newKeys: { access: string; refresh: string };

  constructor(
    tokenStorage: ITokenStorage,
    legacyKeys: { access: string; refresh: string },
    newKeys: { access: string; refresh: string }
  ) {
    this.tokenStorage = tokenStorage;
    this.legacyKeys = legacyKeys;
    this.newKeys = newKeys;
  }

  /**
   * Migrates legacy plain tokens to encrypted storage
   */
  migrate(): { accessToken: string | null; refreshToken: string | null } {
    if (typeof window === 'undefined') {
      return { accessToken: null, refreshToken: null };
    }

    try {
      // Check for legacy tokens in localStorage directly
      const legacyAccessToken = localStorage.getItem(this.legacyKeys.access);
      const legacyRefreshToken = localStorage.getItem(this.legacyKeys.refresh);

      // Migrate access token if needed
      if (legacyAccessToken && !legacyAccessToken.startsWith('enc:')) {
        this.tokenStorage.set(this.newKeys.access, legacyAccessToken);
        // Optionally remove legacy token after migration
        // localStorage.removeItem(this.legacyKeys.access);
      }

      // Migrate refresh token if needed
      if (legacyRefreshToken && !legacyRefreshToken.startsWith('enc:')) {
        this.tokenStorage.set(this.newKeys.refresh, legacyRefreshToken);
        // Optionally remove legacy token after migration
        // localStorage.removeItem(this.legacyKeys.refresh);
      }

      return {
        accessToken: this.tokenStorage.get(this.newKeys.access),
        refreshToken: this.tokenStorage.get(this.newKeys.refresh),
      };
    } catch (error) {
      console.error('[TokenMigrationService] Migration failed:', error);
      return { accessToken: null, refreshToken: null };
    }
  }
}

// ============================================================================
// Factory Functions (Dependency Injection)
// ============================================================================

/**
 * Creates an encryption service instance
 */
const createEncryptionService = (): IEncryptionService => {
  return new EncryptionService();
};

/**
 * Creates a secure token storage instance
 */
const createSecureTokenStorage = (): ITokenStorage => {
  const encryptionService = createEncryptionService();
  const storage =
    typeof window !== 'undefined' ? window.localStorage : ({} as Storage);
  return new SecureTokenStorage(encryptionService, storage);
};

// ============================================================================
// Public API (Facade Pattern - Simple interface for complex subsystem)
// ============================================================================

// Singleton instances (created once, reused)
let tokenStorageInstance: ITokenStorage | null = null;
let migrationServiceInstance: ITokenMigration | null = null;

/**
 * Gets or creates the token storage instance
 */
const getTokenStorage = (): ITokenStorage => {
  if (!tokenStorageInstance) {
    tokenStorageInstance = createSecureTokenStorage();
  }
  return tokenStorageInstance;
};

/**
 * Gets or creates the migration service instance
 */
const getMigrationService = (): ITokenMigration => {
  if (!migrationServiceInstance) {
    const storage = getTokenStorage();
    migrationServiceInstance = new TokenMigrationService(
      storage,
      { access: 'x-access-token', refresh: 'x-refresh-token' },
      { access: 'x-access-token', refresh: 'x-refresh-token' }
    );
  }
  return migrationServiceInstance;
};

// ============================================================================
// Public Exports
// ============================================================================

/**
 * Token key constants
 */
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'x-access-token',
  REFRESH_TOKEN: 'x-refresh-token',
} as const;

/**
 * Securely stores a token
 */
export const setSecureToken = (key: string, token: string | null): void => {
  getTokenStorage().set(key, token);
};

/**
 * Securely retrieves a token
 */
export const getSecureToken = (key: string): string | null => {
  return getTokenStorage().get(key);
};

/**
 * Removes a token
 */
export const removeSecureToken = (key: string): void => {
  getTokenStorage().remove(key);
};

/**
 * Migrates legacy plain tokens to encrypted storage
 */
export const migrateLegacyTokens = (): {
  accessToken: string | null;
  refreshToken: string | null;
} => {
  return getMigrationService().migrate();
};

/**
 * Encrypts a token (for testing or advanced use cases)
 */
export const encryptToken = (token: string): string => {
  const encryptionService = createEncryptionService();
  return encryptionService.encrypt(token);
};

/**
 * Decrypts a token (for testing or advanced use cases)
 */
export const decryptToken = (encryptedToken: string): string | null => {
  const encryptionService = createEncryptionService();
  return encryptionService.decrypt(encryptedToken);
};
